import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { truncate } from "lodash";
import { Table, Tag, Tooltip, Button, Select, message } from "antd";

import { EyeOutlined } from "@ant-design/icons";
import { formatDateTime } from "../../helpers/date";
import { formatTrackingNos } from "../../helpers/orderTracking";
import IssueStatusTag from "../shared/IssueStatusTag";
import { ISSUE_CATEGORIES } from "../../constants/issue";
import { SERVICE_PROVIDER_DETAILS } from "../../constants/serviceProviders";
import { usePermissions } from "../App/PermissionContext";
import { PERMISSIONS } from "../../constants/roles";

const SupportTicketList = ({ supportTickets, loading, assignees }) => {
  const { permissions } = usePermissions();
  const assign = async (ticket, adminId) => {
    await axios
      .post(`/api/issues/assign/${ticket.objectId}`, {
        adminId,
      })
      .then((_) => {
        message.info(`#${ticket.ticketId} succesfully assigned`);
      })
      .catch((error) => {
        message.error(error.response?.data.message || error.message);
      });
  };

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize(); // check on mount
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const table = [
    {
      title: "Ticket ID",
      dataIndex: ["ticketId"],
      width: 140,
      render: (ticketId, record) => (
        <Link to={`/support-tickets/${record.objectId}`}>
          <Tag color="blue">{ticketId}</Tag>
        </Link>
      ),
    },
    {
      title: "Submitted At",
      dataIndex: "createdAt",
      width: 130,
      render: (createdAt) => formatDateTime(createdAt),
    },
    {
      title: "Last Updated At",
      dataIndex: "updatedAt",
      width: 130,
      render: (updatedAt) => formatDateTime(updatedAt),
    },
    {
      title: "Tracking No",
      dataIndex: ["order", "consign_no"],
      width: 180,
      render: (text, record) => formatTrackingNos(record.order) || "-",
    },
    {
      title: "Courier",
      dataIndex: ["order", "serviceProvider"],
      width: 120,
      render: (courier) => {
        const providerName =
          SERVICE_PROVIDER_DETAILS.find((p) => p.key === courier)?.name ||
          courier;

        return providerName;
      },
    },
    {
      title: "User / Merchant",
      dataIndex: ["user"],
      width: 250,
      render: (user) => user ? `${user.email} (${user.countryCode}${user.phone})` : "-",
    },
    {
      title: "Sender Name",
      dataIndex: ["order", "pickup", "fullName"],
      width: 150,
    },
    {
      title: "Description",
      dataIndex: "remark",
      width: 200,
      render: (remark) => <span>{truncate(remark, { length: 100 })}</span>,
    },
    {
      title: "Category",
      dataIndex: ["category"],
      width: 170,
      render: (category) => {
        const categories = [category].flat().filter(Boolean);

        return (
          <>
            {categories.map((c) => {
              const categorySpec = ISSUE_CATEGORIES[c];

              return categorySpec ? (
                <Tag key={c} color={categorySpec.color}>
                  {categorySpec.label}
                </Tag>
              ) : (
                <Tag key={c} color="grey">
                  {c}
                </Tag>
              );
            })}
          </>
        );
      },
    },
    {
      title: "Status",
      dataIndex: ["status"],
      width: 180,
      render: (status) => {
        return <IssueStatusTag statusKey={status} />;
      },
    },
    {
      title: "Comments",
      dataIndex: ["commentSummary", "count"],
      width: 100,
      render: (count) => count || "0",
    },
    {
      title: "Latest Comment",
      dataIndex: ["commentSummary"],
      width: 220,
      render: (commentSummary) => {
        if (!commentSummary.count) {
          return "-";
        }

        return (
          <span>
            {formatDateTime(commentSummary.lastCommentedAt)}
            <br />({commentSummary.lastCommentedBy || "User"})
          </span>
        );
      },
    },
    {
      title: "Submitted By",
      dataIndex: "createdBy",
      width: 130,
      render: (createdBy) => createdBy ? `${createdBy?.email} (${createdBy?.countryCode}${createdBy?.phone})` : "-",
    },
    {
      title: "Assignee",
      dataIndex: "assignedTo",
      width: 165,
      render: (assignedTo, record) => (
        <div>
          {permissions?.includes(PERMISSIONS.ASSIGN_ISSUES) ? (
            <Select
              defaultValue={assignedTo?.objectId || "Assign To..."}
              className="select-before"
              style={{ width: "150px" }}
              onChange={(value) => assign(record, value)}
            >
              {assignees.map((assignee) => (
                <Select.Option key={assignee.id} value={assignee.id}>
                  {assignee.username}
                </Select.Option>
              ))}
            </Select>
          ) : (
            <span>{assignedTo?.username || "-"}</span>
          )}
        </div>
      ),
    },
    {
      title: "",
      dataIndex: "action",
      fixed: isMobile ? undefined : "right",
      width: 80,
      render: (_, record) => (
        <Tooltip title="View">
          <Link to={`/support-tickets/${record.objectId}`}>
            <Button type="primary" icon={<EyeOutlined />} onClick={() => {}} />
          </Link>
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="table">
      <Table
        rowKey="objectId"
        className="table-section"
        columns={table}
        dataSource={supportTickets}
        pagination={false}
        loading={loading}
        scroll={{ x: 2200, y: 600 }}
      />
    </div>
  );
};

export default SupportTicketList;
